/**
 * Historical UTC offsets
 *
 * The offset for a birth moment is whatever the IANA database says it was for
 * that place on that date — via Intl.DateTimeFormat with the zone's name. A
 * fixed +05:30 is wrong for any Indian birth before 1 Jan 1906, wrong again for
 * 1942-45 (+06:30 wartime), and wrong for the 1905-06 transition. Also handles
 * LMT for pre-standard-time births, which is what a careful jyotiṣī would use.
 *
 * ⚠ **The Indian pre-1906 offset is not what this project's notes said it was.**
 * Verified against tzdata, Asia/Kolkata runs:
 *
 *     ≤1854        +05:53:28   LMT, Calcutta meridian
 *     1854–1870    +05:53:20   Howrah Mean Time (Calcutta, 88.36 E / 15)
 *     1870–1906    +05:21:10   Madras Mean Time  (Madras,  80.27 E / 15)
 *     1906–        +05:30:00   IST
 *     1942–1945    +06:30:00   wartime
 *
 * So +05:53:20 is **Calcutta**, not Madras, and it ended in **1870**, not 1906.
 * The offset in force for most pre-IST births is +05:21:10. Using 05:53:20 for
 * an 1890 birth is 32 minutes out, which moves the lagna by roughly 8° — a
 * quarter of a rāśi, on the one angle the whole chart is built from. This is
 * why the offset is looked up and never written down.
 *
 * © Manish Jagdish Thatte
 */

const MS_PER_MIN = 60000;

const formatterCache = new Map();
function offsetFormatter(zone) {
  let f = formatterCache.get(zone);
  if (!f) {
    // Throws RangeError for an unknown zone, which is what we want — a typo in
    // a zone name must never silently become UTC.
    f = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' });
    formatterCache.set(zone, f);
  }
  return f;
}

/**
 * Minutes east of UTC for `zone` at `instant`, as the IANA database records it
 * for that date. `instant` is a Date or epoch-ms.
 *
 * Browsers and Node ship the full tzdata history, so this is correct back to
 * the start of the database at no cost to us.
 */
export function offsetAt(zone, instant) {
  const date = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(date.getTime())) throw new TypeError('offsetAt: invalid instant');

  const part = offsetFormatter(zone)
    .formatToParts(date)
    .find((p) => p.type === 'timeZoneName');
  if (!part) throw new Error(`offsetAt: no offset available for ${zone}`);

  // 'GMT+05:30', 'GMT-04:39:17' (LMT-era zones carry seconds), or bare 'GMT'.
  const m = /^GMT(?:([+-])(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?)?$/.exec(part.value);
  if (!m) throw new Error(`offsetAt: cannot parse offset "${part.value}" for ${zone}`);
  if (!m[1]) return 0;

  const sign = m[1] === '-' ? -1 : 1;
  const minutes = Number(m[2]) * 60 + Number(m[3] || 0) + Number(m[4] || 0) / 60;
  return sign * minutes;
}

/**
 * Local wall-clock time in `zone` → the UTC instant.
 *
 * Returns `{ instant, offsetMinutes, ambiguous, skipped, alternatives }`.
 *
 * ⚠ A local time inside a fall-back hour maps to **two** instants and a local
 * time inside a spring-forward gap maps to **none**. Both are surfaced, never
 * silently resolved: `instant` carries the earlier candidate so callers that
 * ignore the flags still get something defensible, but a chart that quietly
 * picked the wrong side of a DST fold is an hour wrong and looks fine.
 *
 * `localISO` is 'YYYY-MM-DDTHH:MM' or 'YYYY-MM-DDTHH:MM:SS', with **no** zone
 * suffix — it is a wall clock reading, not an instant.
 */
export function localToUTC(localISO, zone) {
  const m = /^(-?\d{4,6})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}(?:\.\d+)?))?$/.exec(
    String(localISO).trim()
  );
  if (!m) throw new TypeError(`localToUTC: expected 'YYYY-MM-DDTHH:MM[:SS]', got "${localISO}"`);

  const [, yy, mo, dd, hh, mi, ss] = m;
  // The wall-clock reading interpreted as if it were UTC. Every candidate
  // instant is this minus some offset.
  const asUTC = Date.UTC(Number(yy), Number(mo) - 1, Number(dd), Number(hh), Number(mi), 0, 0)
    + Math.round(Number(ss || 0) * 1000);
  // Date.UTC maps years 0-99 into 1900-1999; undo that for ancient dates.
  const wall = Number(yy) >= 0 && Number(yy) < 100
    ? adjustLowYear(asUTC, Number(yy))
    : asUTC;

  // Two probes bracket every real transition: the offset an hour-scale distance
  // either side of the naive guess.
  const candidates = new Set();
  for (const probe of [wall - 86400000, wall, wall + 86400000]) {
    const off = offsetAt(zone, probe);
    candidates.add(wall - off * MS_PER_MIN);
  }

  // A candidate is real only if reading it back in the zone gives the wall clock
  // we started from. This is what rejects the gap and confirms the fold.
  const valid = [];
  for (const c of candidates) {
    if (c + offsetAt(zone, c) * MS_PER_MIN === wall) valid.push(c);
  }
  valid.sort((a, b) => a - b);

  if (valid.length === 0) {
    // Spring-forward gap: the wall clock never happened. Report the instant the
    // clock jumped to, which is the only sensible thing to show.
    const off = offsetAt(zone, wall);
    const instant = new Date(wall - off * MS_PER_MIN);
    return { instant, offsetMinutes: off, ambiguous: false, skipped: true, alternatives: [] };
  }

  const instant = new Date(valid[0]);
  return {
    instant,
    offsetMinutes: offsetAt(zone, valid[0]),
    ambiguous: valid.length > 1,
    skipped: false,
    alternatives: valid.slice(1).map((c) => ({
      instant: new Date(c),
      offsetMinutes: offsetAt(zone, c)
    }))
  };
}

function adjustLowYear(ms, year) {
  const d = new Date(ms);
  d.setUTCFullYear(year);
  return d.getTime();
}

/**
 * Local Mean Time offset for a meridian, in minutes east.
 *
 * ⚠ This is frequently the *right* answer rather than a fallback. Before a
 * country adopted standard time, a birth record's "7:12" was read off a clock
 * set by the local sun. JHora's own bundled Gandhi chart uses −4.654444 h for
 * Porbandar — LMT, not IST, and decisively not the +05:53:20 that the project
 * notes attributed to that era.
 *
 * ⚠ But JHora's two fields do **not** agree with each other: that file stores
 * longitude 69.49 E, and 69.49 / 15 = 4.632667 h, not 4.654444 h. JHora's
 * 4.654444 h corresponds to 69.8167 E. The offset is therefore an independent
 * stored value, not something JHora derives from the longitude — so the `.jhd`
 * writer must round-trip both fields rather than computing one from the other.
 */
export function lmtOffset(lonEast) {
  if (!Number.isFinite(lonEast)) throw new TypeError('lmtOffset: lonEast must be finite');
  if (lonEast < -180 || lonEast > 180) throw new RangeError(`lmtOffset: ${lonEast} out of range`);
  return lonEast * 4; // 360° / 24 h = 15°/h, so 1° = 4 minutes
}

/**
 * When did this zone adopt a standard offset? Returns the first instant at or
 * after `from` whose offset is a whole number of minutes and stays put for a
 * year — a cheap proxy for "LMT era ended", used only to decide whether to
 * *suggest* LMT in the UI. Never used to silently switch.
 */
export function looksLikeLMT(zone, instant) {
  const off = offsetAt(zone, instant);
  // LMT-era zones carry a fractional-minute offset (Asia/Kolkata was +05:53:20
  // until 1906, i.e. 353.333 minutes).
  return Math.abs(off - Math.round(off)) > 1e-9;
}
